import { animate, style, transition, trigger } from '@angular/animations';
import {
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { ThematicListDto } from 'app/common/generated';
import { TranslocoModule } from '@jsverse/transloco';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NoDataComponent } from 'app/common/components/no-data/no-data.component';
import { ListDisplayMode } from 'app/common/types';
import { ThematicAppService } from '../thematic.app.service';

/** Animation de la bandelette compacte : entre/sort par la gauche avec un effet de scale */
const compactAnim = trigger('compactAnim', [
    transition(':enter', [
        style({
            opacity: 0,
            transform: 'translateX(-20px) scaleX(0.7)',
            transformOrigin: 'left center',
        }),
        animate(
            '220ms cubic-bezier(0.4, 0, 0.2, 1)',
            style({ opacity: 1, transform: 'translateX(0) scaleX(1)' })
        ),
    ]),
    transition(':leave', [
        animate(
            '180ms cubic-bezier(0.4, 0, 0.2, 1)',
            style({
                opacity: 0,
                transform: 'translateX(-20px) scaleX(0.7)',
                transformOrigin: 'left center',
            })
        ),
    ]),
]);

/** Animation de la grille large : entre/sort par la droite */
const largeAnim = trigger('largeAnim', [
    transition(':enter', [
        style({ opacity: 0, transform: 'translateX(28px)' }),
        animate(
            '240ms 60ms cubic-bezier(0.4, 0, 0.2, 1)',
            style({ opacity: 1, transform: 'translateX(0)' })
        ),
    ]),
    transition(':leave', [
        animate(
            '180ms cubic-bezier(0.4, 0, 0.2, 1)',
            style({ opacity: 0, transform: 'translateX(28px)' })
        ),
    ]),
]);

@Component({
    selector: 'app-thematic-list',
    standalone: true,
    imports: [TranslocoModule, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule, NoDataComponent],
    templateUrl: './thematic-list.component.html',
    styleUrls: ['./thematic-list.component.scss'],
    animations: [...fuseAnimations, compactAnim, largeAnim],
})
export class ThematicListComponent {
    private readonly thematicService = inject(ThematicAppService);

    /** Liste des thématiques à afficher */
    public readonly thematics = input<ThematicListDto[]>([]);

    /** Mode d'affichage : 'large' = grille complète, 'compact' = bouton nom incliné */
    public readonly mode = input<ListDisplayMode>('large');

    /** Thématique actuellement sélectionnée (contrôlée par le parent) */
    public readonly current = input<ThematicListDto | null>(null);

    public readonly sectorId = input<string | null>(null);

    // ─── Événements ─────────────────────────────────────────────────────────────

    /** Émis quand une thématique est sélectionnée en mode large */
    readonly selected = output<ThematicListDto>();

    /** Émis lors du clic sur le bouton en mode compact (thématique sélectionnée ou null) */
    readonly released = output<ThematicListDto | null>();

    readonly refresh = output<void>();

    // ─── État interne ────────────────────────────────────────────────────────────

    /** Terme de recherche (utilisé uniquement en mode large) */
    public readonly searchQuery = signal('');

    /** Liste filtrée selon la recherche */
    public readonly filteredThematics = computed(() => {
        const query = this.searchQuery().toLowerCase().trim();
        if (!query) return this.thematics();
        return this.thematics().filter(
            (t) =>
                t.name?.toLowerCase().includes(query) ||
                t.description?.toLowerCase().includes(query)
        );
    });

    // ─── Handlers ────────────────────────────────────────────────────────────────

    public onSelect(thematic: ThematicListDto): void {
        this.selected.emit(thematic);
    }

    public onAdd(): void {
        this.thematicService
            .create(this.sectorId() || undefined)
            .subscribe((result) => {
                if (result) {
                    // TODO: The 'emit' function requires a mandatory void argument
                    this.refresh.emit();
                }
            });
    }

    public onEdit(thematic: ThematicListDto, event: Event): void {
        event.stopPropagation();
        this.thematicService.update(thematic.id).subscribe((result) => {
            if (result) {
                // TODO: The 'emit' function requires a mandatory void argument
                this.refresh.emit();
            }
        });
    }

    public onDelete(thematic: ThematicListDto, event: Event): void {
        event.stopPropagation();
        this.thematicService.delete(thematic.id).subscribe((result) => {
            if (result) {
                // TODO: The 'emit' function requires a mandatory void argument
                this.refresh.emit();
            }
        });
    }

    public onCompactClick(): void {
        this.released.emit(this.current());
    }
}
